import React, { useState } from 'react';
import { ArrowLeft, Bell, Check, Play, CalendarDays, BookOpen, ClipboardCheck, Award, Volume2, PenLine, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { centerToast } from '../components/CenterToast';
import AppLogo from '../components/AppLogo';
import { ulumIhsanData } from '../data/mockData';

const MOODS = [
  { id: 'great', label: 'Great!', emoji: '😄' },
  { id: 'good', label: 'Good', emoji: '🙂' },
  { id: 'okay', label: 'Okay', emoji: '😐' },
  { id: 'not-great', label: 'Not great', emoji: '😕' },
  { id: 'sad', label: 'Sad', emoji: '😢' }
];

function CourseHeader({ onBack, counter }) {
  return (
    <div className="bg-white px-4 pt-4 pb-3 flex items-center justify-between border-b border-border/60">
      <div className="flex items-center gap-3">
        <button type="button" data-testid="course-back-button" onClick={onBack} aria-label="Back">
          <ArrowLeft className="h-5 w-5 text-[#0F172A]" />
        </button>
        <AppLogo size="sm" />
        <span className="text-base font-extrabold leading-none">
          <span className="text-[#0F172A]">Ihsan</span>
          <span className="text-primary">One</span>
        </span>
      </div>
      {counter && <span className="text-xs font-semibold text-muted-foreground">{counter}</span>}
    </div>
  );
}

function StatChip({ icon: Icon, label }) {
  return (
    <div className="rounded-xl bg-muted/60 p-2.5 flex flex-col items-center text-center gap-1">
      <Icon className="h-4 w-4 text-primary" />
      <span className="text-[10px] font-semibold text-foreground leading-tight">{label}</span>
    </div>
  );
}

export default function UlumIhsanCourse({ onExit }) {
  const data = ulumIhsanData;
  const [view, setView] = useState('home');
  const [fardhuStarted, setFardhuStarted] = useState(false);
  const [duaStarted, setDuaStarted] = useState(false);
  const [duaVideoOpen, setDuaVideoOpen] = useState(false);
  const [activeLessonIdx, setActiveLessonIdx] = useState(0);
  const [attendanceMarked, setAttendanceMarked] = useState(false);
  const [mood, setMood] = useState(null);
  const [feedback, setFeedback] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const todayLabel = new Date().toLocaleDateString('en-SG', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });

  const handleOpenLesson = (idx, started) => {
    if (!started) {
      centerToast('Tap "Start Learning" first to unlock the lessons', '🔒');
      return;
    }
    setActiveLessonIdx(idx);
    setView('fardhu-lesson');
  };

  const handleSubmitAttendanceFeedback = () => {
    if (!mood) {
      toast.error('Please select how you are feeling today', { position: 'top-center' });
      return;
    }
    setSubmitted(true);
    centerToast(`Attendance & feedback submitted. Thank you, ${data.learnerName}!`, '🙏');
  };

  if (view === 'home') {
    return (
      <div className="min-h-screen bg-background page-fade-in" data-testid="ulum-ihsan-home">
        <div className="bg-white px-4 pt-4 pb-3 flex items-center justify-between border-b border-border/60">
          <button type="button" data-testid="course-back-button" onClick={onExit} aria-label="Back">
            <ArrowLeft className="h-5 w-5 text-[#0F172A]" />
          </button>
          <span className="relative">
            <Bell className="h-5 w-5 text-[#0F172A]" />
            <span className="absolute -top-1 -right-1 min-w-[14px] h-3.5 px-1 rounded-full bg-rose-500 text-white text-[9px] font-bold flex items-center justify-center">3</span>
          </span>
        </div>

        <div className="px-4 pt-5">
          <h1 className="text-xl font-extrabold text-[#0F172A]">Assalamualaikum, {data.learnerName}! 👋</h1>
          <p className="text-sm text-muted-foreground mt-1">Keep learning, keep growing!</p>
        </div>

        <div className="px-4 pt-4">
          <div className="rounded-xl bg-primary/5 border border-primary/20 p-3 flex items-center gap-3" data-testid="registered-learner-banner">
            <div className="w-9 h-9 rounded-lg bg-primary/15 text-primary flex items-center justify-center flex-shrink-0 text-lg">🌱</div>
            <div>
              <p className="text-xs font-bold text-[#0F172A]">You&apos;re a registered learner</p>
              <p className="text-[11px] text-muted-foreground">Keep completing your lessons and activities to earn your learning badges!</p>
            </div>
          </div>
        </div>

        <div className="px-4 pt-5">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-bold text-[#0F172A]">My Learning Journey</h2>
            <span className="text-xs text-muted-foreground">{data.modulesCompleted} of {data.modulesTotal} completed</span>
          </div>
          <div className="h-2 w-full rounded-full bg-muted overflow-hidden" data-testid="learning-progress-bar">
            <div
              className="h-full bg-primary rounded-full transition-all duration-500"
              style={{ width: `${(data.modulesCompleted / data.modulesTotal) * 100}%` }}
            />
          </div>
        </div>

        <div className="px-4 pt-5 flex flex-col gap-3">
          <button type="button" data-testid="module-card-fardhu-ain" onClick={() => setView('fardhu-ain')} className="relative rounded-xl overflow-hidden h-20 text-left">
            <img src="https://images.unsplash.com/photo-1569929919600-59123640bc02?w=600&q=80" alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-teal-700/90 to-teal-700/40" />
            <div className="relative h-full flex items-center gap-3 px-4">
              <span className="w-7 h-7 rounded-full bg-white text-primary flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
              <div>
                <p className="text-sm font-bold text-white">Fardhu Ain</p>
                <p className="text-[11px] text-white/80">Essentials of Islamic knowledge &amp; practices</p>
              </div>
            </div>
          </button>

          <button type="button" data-testid="module-card-dua-surah" onClick={() => setView('dua-surah')} className="relative rounded-xl overflow-hidden h-20 text-left">
            <img src="https://images.unsplash.com/photo-1589995635011-078e0bb91d11?w=600&q=80" alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-indigo-800/90 to-indigo-800/40" />
            <div className="relative h-full flex items-center gap-3 px-4">
              <span className="w-7 h-7 rounded-full bg-white text-indigo-800 flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
              <div>
                <p className="text-sm font-bold text-white">Dua &amp; Surah Reading</p>
                <p className="text-[11px] text-white/80">Basic reading proficiency</p>
              </div>
            </div>
          </button>

          <button type="button" data-testid="module-card-attendance" onClick={() => setView('attendance')} className="relative rounded-xl overflow-hidden h-20 text-left bg-amber-500">
            <div className="relative h-full flex items-center gap-3 px-4">
              <span className="w-7 h-7 rounded-full bg-white text-amber-600 flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
              <div>
                <p className="text-sm font-bold text-white">Attendance &amp; Feedback</p>
                <p className="text-[11px] text-white/85">Track your progress</p>
              </div>
            </div>
          </button>
        </div>

        <div className="px-4 pt-5 pb-8">
          <div className="rounded-xl bg-muted/60 p-3 text-center">
            <p className="text-xs font-semibold text-[#0F172A]">⭐ Small steps, big blessings!</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">You&apos;re doing great, {data.learnerName}!</p>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'fardhu-ain') {
    const mod = data.fardhuAin;
    return (
      <div className="min-h-screen bg-background page-fade-in" data-testid="fardhu-ain-page">
        <CourseHeader onBack={() => setView('home')} counter={`${fardhuStarted ? 1 : 0}/${mod.lessons.length}`} />
        <div className="relative h-40">
          <img src={mod.heroImage} alt="" className="w-full h-full object-cover" />
          <div className="absolute bottom-3 left-4 flex items-center gap-2">
            <span className="px-2.5 py-1 rounded-md text-[11px] font-bold text-white bg-primary">Fardhu Ain</span>
            <span className="px-2.5 py-1 rounded-md text-[11px] font-bold text-primary bg-white">{mod.level}</span>
          </div>
        </div>
        <div className="px-4 pt-4 pb-8">
          <h1 className="text-lg font-extrabold text-[#0F172A] leading-tight">{mod.title}</h1>
          <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{mod.description}</p>

          <div className="grid grid-cols-3 gap-2 mt-4">
            <StatChip icon={BookOpen} label={`${mod.lessons.length} Lessons`} />
            <StatChip icon={ClipboardCheck} label="Quizzes" />
            <StatChip icon={Award} label="Badge on completion" />
          </div>

          <h3 className="text-sm font-bold text-[#0F172A] mt-5 mb-2">What you&apos;ll learn</h3>
          <div className="flex flex-col gap-2">
            {mod.lessons.map((lesson, idx) => (
              <button
                key={lesson.id}
                type="button"
                data-testid={`fardhu-lesson-${lesson.id}`}
                onClick={() => handleOpenLesson(idx, fardhuStarted)}
                className={`flex items-center gap-3 p-3 rounded-xl border border-border text-left transition-opacity ${fardhuStarted ? '' : 'opacity-60'}`}
              >
                <span className="w-7 h-7 rounded-full bg-primary text-white flex items-center justify-center text-xs font-bold flex-shrink-0">{idx + 1}</span>
                <span className="flex-1 text-sm font-medium text-foreground">{lesson.title}</span>
                <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              </button>
            ))}
          </div>

          <button
            type="button"
            data-testid="start-learning-button"
            onClick={() => { setFardhuStarted(true); centerToast('Course started! Tap any lesson to begin.', '📖'); }}
            className="w-full h-12 rounded-xl bg-primary text-white font-bold text-sm mt-5"
          >
            {fardhuStarted ? '▶ Learning In Progress' : '▶ Start Learning'}
          </button>
        </div>
      </div>
    );
  }

  if (view === 'fardhu-lesson') {
    const lessons = data.fardhuAin.lessons;
    const lesson = lessons[activeLessonIdx];
    return (
      <div className="min-h-screen bg-background page-fade-in" data-testid="fardhu-lesson-page">
        <CourseHeader onBack={() => setView('fardhu-ain')} counter={`${activeLessonIdx + 1}/${lessons.length}`} />
        <div className="px-4 pt-4">
          <span className="px-2.5 py-1 rounded-md text-[11px] font-bold text-white bg-primary">Lesson {activeLessonIdx + 1}</span>
          <h1 className="text-lg font-extrabold text-[#0F172A] mt-2 leading-tight">{lesson.title}</h1>
        </div>
        <div className="px-4 pt-3">
          <div className="rounded-xl overflow-hidden aspect-video bg-black" data-testid="lesson-video-embed">
            <iframe
              width="100%"
              height="100%"
              src={`https://www.youtube.com/embed/${lesson.videoId}`}
              title={lesson.title}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
          <p className="text-sm text-foreground leading-relaxed mt-4">{lesson.description}</p>
        </div>
        <div className="px-4 pt-6 pb-8 flex gap-3">
          <button
            type="button"
            data-testid="lesson-prev-button"
            disabled={activeLessonIdx === 0}
            onClick={() => setActiveLessonIdx((i) => i - 1)}
            className="flex-1 h-11 rounded-xl border border-border text-sm font-semibold disabled:opacity-40"
          >
            Previous
          </button>
          {activeLessonIdx < lessons.length - 1 ? (
            <button
              type="button"
              data-testid="lesson-next-button"
              onClick={() => setActiveLessonIdx((i) => i + 1)}
              className="flex-1 h-11 rounded-xl bg-primary text-white text-sm font-semibold"
            >
              Next Lesson
            </button>
          ) : (
            <button
              type="button"
              data-testid="lesson-complete-button"
              onClick={() => { centerToast('Fardhu Ain module completed!', '🎓'); setView('fardhu-ain'); }}
              className="flex-1 h-11 rounded-xl bg-primary text-white text-sm font-semibold"
            >
              Finish Module
            </button>
          )}
        </div>
      </div>
    );
  }

  if (view === 'dua-surah') {
    const mod = data.duaSurah;
    return (
      <div className="min-h-screen bg-background page-fade-in" data-testid="dua-surah-page">
        <CourseHeader onBack={() => setView('home')} counter="1/6" />
        <div className="relative h-40">
          <img src={mod.heroImage} alt="" className="w-full h-full object-cover" />
          <div className="absolute bottom-3 left-4 flex items-center gap-2">
            <span className="px-2.5 py-1 rounded-md text-[11px] font-bold text-white bg-indigo-800">Dua &amp; Surah Reading</span>
            <span className="px-2.5 py-1 rounded-md text-[11px] font-bold text-indigo-800 bg-white">{mod.level}</span>
          </div>
        </div>
        <div className="px-4 pt-4 pb-8">
          <h1 className="text-lg font-extrabold text-[#0F172A] leading-tight">{mod.title}</h1>
          <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{mod.description}</p>

          <div className="grid grid-cols-3 gap-2 mt-4">
            <StatChip icon={BookOpen} label="6 Lessons" />
            <StatChip icon={Volume2} label="Audio Guide" />
            <StatChip icon={PenLine} label="Practice Exercises" />
          </div>

          <h3 className="text-sm font-bold text-[#0F172A] mt-5 mb-2">This Week&apos;s Lesson</h3>
          <div className="rounded-xl border border-border p-3 flex items-center gap-3">
            <div className="w-11 h-11 rounded-lg bg-indigo-100 text-indigo-700 flex items-center justify-center flex-shrink-0">
              <BookOpen className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-bold text-[#0F172A]">{mod.weeklyLesson.title}</p>
              <p className="text-[11px] text-muted-foreground">{mod.weeklyLesson.description}</p>
            </div>
          </div>

          <div className="mt-3 rounded-xl overflow-hidden border border-border" data-testid="dua-video-card">
            {duaVideoOpen ? (
              <div className="aspect-video bg-black">
                <iframe
                  width="100%"
                  height="100%"
                  src={`https://www.youtube.com/embed/${mod.weeklyLesson.videoId}?autoplay=1`}
                  title={mod.weeklyLesson.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ) : (
              <button
                type="button"
                data-testid="dua-video-play-button"
                onClick={() => {
                  if (!duaStarted) {
                    centerToast('Tap "Start Lesson" first to unlock the video', '🔒');
                    return;
                  }
                  setDuaVideoOpen(true);
                }}
                className="relative w-full h-36 block"
              >
                <img src={`https://img.youtube.com/vi/${mod.weeklyLesson.videoId}/hqdefault.jpg`} alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                  <span className="w-11 h-11 rounded-full bg-white/90 flex items-center justify-center">
                    <Play className="h-5 w-5 text-primary fill-primary" />
                  </span>
                </div>
                <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between">
                  <span className="text-white text-xs font-semibold drop-shadow">{mod.weeklyLesson.title} (Part 1)</span>
                  <span className="text-white text-[11px] bg-black/50 px-1.5 py-0.5 rounded">{mod.weeklyLesson.duration}</span>
                </div>
              </button>
            )}
          </div>
        </div>

        <div className="px-4 pt-2 pb-8">
          <button
            type="button"
            data-testid="start-lesson-button"
            onClick={() => { setDuaStarted(true); centerToast('Lesson started! Tap the video to begin.', '🎬'); }}
            className="w-full h-12 rounded-xl bg-primary text-white font-bold text-sm"
          >
            {duaStarted ? '▶ Lesson In Progress' : '▶ Start Lesson'}
          </button>
        </div>
      </div>
    );
  }

  if (view === 'attendance') {
    return (
      <div className="min-h-screen bg-background page-fade-in" data-testid="attendance-page">
        <CourseHeader onBack={() => setView('home')} counter="1/3" />
        <div className="px-4 pt-4 flex items-center gap-2">
          <span className="px-2.5 py-1 rounded-md text-[11px] font-bold text-white bg-amber-500">Attendance &amp; Feedback</span>
          <span className="px-2.5 py-1 rounded-md text-[11px] font-bold text-amber-600 bg-amber-50">Level 1</span>
        </div>
        <div className="px-4 pt-3">
          <h1 className="text-lg font-extrabold text-[#0F172A]">Track your progress</h1>
          <p className="text-sm text-muted-foreground mt-1">Let us know you&apos;re here and how you feel. Your feedback helps us make learning better for you!</p>
        </div>

        <div className="px-4 pt-4">
          <h3 className="text-sm font-bold text-[#0F172A] mb-2">Today&apos;s Attendance</h3>
          <div className="rounded-xl border border-border p-3 flex items-center justify-between" data-testid="attendance-card">
            <div className="flex items-center gap-3">
              <CalendarDays className="h-5 w-5 text-muted-foreground" />
              <span className="text-sm font-medium text-[#0F172A]">{todayLabel}</span>
            </div>
            <button
              type="button"
              data-testid="mark-present-button"
              onClick={() => setAttendanceMarked((v) => !v)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
                attendanceMarked ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
              }`}
            >
              <Check className="h-3.5 w-3.5" /> {attendanceMarked ? 'Present' : 'Mark Present'}
            </button>
          </div>
        </div>

        <div className="px-4 pt-5">
          <h3 className="text-sm font-bold text-[#0F172A] mb-2">How are you feeling today?</h3>
          <div className="flex justify-between gap-1.5" data-testid="mood-selector">
            {MOODS.map((m) => (
              <button
                key={m.id}
                type="button"
                data-testid={`mood-${m.id}`}
                onClick={() => setMood(m.id)}
                className={`flex-1 flex flex-col items-center gap-1 py-2 rounded-xl border transition-colors ${
                  mood === m.id ? 'border-primary bg-primary/10' : 'border-border'
                }`}
              >
                <span className="text-2xl leading-none">{m.emoji}</span>
                <span className="text-[10px] font-medium text-foreground">{m.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="px-4 pt-5">
          <h3 className="text-sm font-bold text-[#0F172A] mb-2">Any feedback for us?</h3>
          <textarea
            data-testid="feedback-textarea"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value.slice(0, 200))}
            placeholder="Share your thoughts (optional)..."
            rows={3}
            className="w-full rounded-xl bg-muted/60 p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
          <p className="text-right text-[11px] text-muted-foreground mt-1">{feedback.length}/200</p>
        </div>

        <div className="px-4 pt-3 pb-8">
          <button
            type="button"
            data-testid="submit-attendance-feedback-button"
            onClick={handleSubmitAttendanceFeedback}
            disabled={submitted}
            className={`w-full h-12 rounded-xl font-bold text-sm transition-colors ${
              submitted ? 'bg-primary/20 text-primary cursor-default' : 'bg-primary text-white hover:bg-primary-hover'
            }`}
          >
            {submitted ? 'Submitted ✓' : 'Submit'}
          </button>
        </div>
      </div>
    );
  }

  return null;
}
